import { useEffect, useRef, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { Check, ChevronDown, Globe } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { LOCALES, localizePath } from './locales'
import { useLocale, useLocalPathname } from './Link'

/** Dropdown listing every language; each entry links to the same page in that language. */
export default function LanguageSwitcher({ compact = false }: { compact?: boolean }) {
  const { t } = useTranslation('common')
  const locale = useLocale()
  const path = useLocalPathname()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const close = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false)
    }
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', close)
    document.addEventListener('keydown', esc)
    return () => {
      document.removeEventListener('mousedown', close)
      document.removeEventListener('keydown', esc)
    }
  }, [open])

  return (
    <div className={`lang-switcher${compact ? ' compact' : ''}`} ref={ref}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('language.label')}
        onClick={() => setOpen(!open)}
      >
        <Globe size={16} />
        <span>{compact ? locale.code.toUpperCase() : locale.name}</span>
        <ChevronDown size={14} />
      </button>
      {/* Always in the DOM (hidden when closed) so crawlers can follow every language link. */}
      <ul role="listbox" aria-label={t('language.label')} hidden={!open}>
          {LOCALES.map((l) => (
            <li key={l.code} role="option" aria-selected={l.code === locale.code}>
              <RouterLink
                to={localizePath(l.code, path)}
                hrefLang={l.hreflang}
                lang={l.htmlLang}
                onClick={() => setOpen(false)}
              >
                {l.name}
                {l.code === locale.code && <Check size={14} />}
              </RouterLink>
            </li>
          ))}
        </ul>
    </div>
  )
}
